import { Injectable } from '@angular/core';
import { CoachClientSlot, isSlotSleeping } from '@cf-shared/data-model/coach';
import { describeError } from '@cf-shared/error-describe';
import { getMyApp } from 'app/app-interface';
import { LL } from 'i18n/i18n.service';
import { untranslatedString } from 'models/translated-string';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { LoadingController } from 'providers/loading-controller';
import { COACH_DEEP_LINK_PAGE_KEY } from './coach-manage-deep-link.service';
import { LoggingService } from './logging.service';
import { OnlineStateService } from './online-state.service';
import { UserDb } from './user-db.service';

/**
 * A coach entering one of their clients' accounts from the coach's own screens: the Manage Client
 * button, Review Intake, Fill Intake Myself and an action-item row on the client detail page.
 *
 * This is the single chokepoint for entering a client's account from those screens, so the
 * refusals (a parked client, no connection) and the admin-impersonation snapshot download are
 * written once. A notification link enters through `CoachManageDeepLinkService` instead.
 */
@Injectable({ providedIn: 'root' })
export class CoachClientEntryService {
  constructor(
    private userDb: UserDb,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private onlineState: OnlineStateService,
    private logging: LoggingService,
  ) {
  }

  /**
   * Whether the coach may act on this client, for any action that reaches into
   * the client's own account (#4530).
   *
   * Returns true to carry on. Returns false — having already shown the refusal
   * — when the client is parked.
   *
   * Named as a question rather than as "refuseIfAsleep", so the call sites read
   * the right way round: `if (!await canActOnClientAsync(slot)) return;` says
   * "if I may not act, stop", which is what happens. The refusing name made the
   * same line read as its own opposite, and this helper exists precisely so a
   * future action can be covered by adding one call — which means getting its
   * sense from the name alone has to be safe.
   *
   * One helper rather than the alert written out at each site, so the wording is
   * decided once.
   */
  async canActOnClientAsync(slot: CoachClientSlot): Promise<boolean> {
    if (!isSlotSleeping(slot)) return true;
    await this.alertCtrl.showAlertAsync({
      title: LL.coach.clientAsleep.refusedTitle(),
      message: LL.coach.clientAsleep.refusedMessage(),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
    return false;
  }

  /**
   * Enter this client's account, and open `page` there once the app has restarted (the stashed
   * deep-link form, see `coach-manage-deep-link.ts`; 'intake-review' is the intake form in change
   * mode). Returns false when entry was refused, leaving the coach where they are.
   */
  async enterClientAccountAsync(slot: CoachClientSlot, page?: string): Promise<boolean> {
    // stash the page before entering: navigateToDeepLinkPageAsync (in coach-manage-deep-link.service)
    // opens it on the boot after the restart
    if (page) {
      sessionStorage.setItem(COACH_DEEP_LINK_PAGE_KEY, page);
    }
    const entered = await this.manageClientAsync(slot);
    // if we refused to enter (offline), take the deep-link marker back out. Leaving it behind
    // would make the NEXT manage-mode entry — possibly for a different client, possibly days
    // later — jump straight to that page for no reason the coach can explain.
    if (page && !entered) {
      sessionStorage.removeItem(COACH_DEEP_LINK_PAGE_KEY);
    }
    return entered;
  }

  /** Enter manage mode for this client. Returns false when we refused to (offline).
   *
   * F23 (#3991): entering manage mode triggers a full app reload that clears warm resume and
   * then loads the client's data from the server. Offline none of that can work, and the
   * failure surfaced as a confusing stuck boot — the coach's own app appearing to hang. Check
   * first and say what is wrong, leaving the coach where they are. */
  private async manageClientAsync(slot: CoachClientSlot): Promise<boolean> {
    // #4530: a parked client cannot be opened. Refused in the same shape as the
    // offline refusal below — an alert that names the fix, and the coach is left
    // where they are.
    //
    // See `design-docs/coach-mode.md` → "Sleeping clients" for the limit this
    // refusal has: it HIDES the client from their coach, it does not take the
    // coach's database access away, so it must never be described as a
    // security control.
    if (!await this.canActOnClientAsync(slot)) {
      return false;
    }

    // re-check before refusing — the cached state can sit stale for minutes (the foreground tick
    // re-checks an Offline belief, but only every six minutes), and wrongly blocking a coach who is
    // actually online would be worse than the confusing boot we fixed
    if (!await this.onlineState.isLikelyOnlineOrRecheckAsync()) {
      await this.alertCtrl.showAlertAsync({
        title: LL.coach.manageClientOffline.title(),
        message: LL.coach.manageClientOffline.message(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return false;
    }

    // #4192: an admin/support account impersonating a coach is NOT a CouchDB
    // member of that coach's client DBs, so the normal live-sync manage mode
    // would 403 on the client's DB and boot to "App failed to start". Download
    // the client's DB into a local read-only snapshot first (officer-gated
    // server download with admin creds), the same pre-reload download admin
    // impersonation already does for the coach's own DB. A real coach (not
    // impersonating) skips this and keeps the live, editable manage mode.
    // Untranslated strings on purpose — this branch is admin-only, like the
    // rest of the impersonation UI.
    if (this.userDb.isImpersonating()) {
      const loader = this.loadingCtrl.create({
        content: untranslatedString('Loading client snapshot for impersonation. Please wait...'),
      });
      // present outside the try (and awaited) so the spinner is visible before the
      // download starts; a present() failure is not a download failure and stays
      // out of the catch below. Mirrors the client detail page's removeClient loader pattern.
      await loader.present();
      try {
        await this.userDb.downloadUserIntoImpersonationDbAsync(slot.clientUid);
      } catch (err) {
        // do not enter a half-loaded manage session — surface to Sentry and the
        // admin, and stay on this page (never boot into the fatal error screen).
        this.logging.trackAbnormalException(err);
        const message = describeError(err);
        void this.alertCtrl.showAlertAsync({
          message: untranslatedString(`Could not load client data for impersonation: ${message}`),
        });
        return false;
      } finally {
        // dismiss on every exit from the download attempt (success, throw, early
        // return). fire-and-forget like removeClient — a dismiss() rejection must
        // not replace the outcome or block the reload below.
        void loader.dismiss();
      }
    }

    this.userDb.setCoachManagingClient({
      clientUid: slot.clientUid,
      clientDisplayName: slot.nickname,
    });
    await getMyApp().reloadAppAsync({ resetToRoot: true });
    return true;
  }
}
